import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, Link } from "react-router-dom";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import "./AdminImageEditor.css";

// ==================== CONSTANTS ====================
const FILTERS = {
  none: {
    name: "Normal",
    style: {},
    preview: "brightness(100%)"
  },
  grayscale: {
    name: "N&B",
    style: { filter: "grayscale(100%)" },
    preview: "grayscale(100%)"
  },
  sepia: {
    name: "Sépia",
    style: { filter: "sepia(100%)" },
    preview: "sepia(100%)"
  },
  invert: {
    name: "Inverser",
    style: { filter: "invert(100%)" },
    preview: "invert(100%)"
  },
  blur: {
    name: "Flou",
    style: { filter: "blur(2px)" },
    preview: "blur(1px)"
  },
  sharpen: {
    name: "Netteté",
    style: { filter: "contrast(150%) brightness(110%)" },
    preview: "contrast(150%) brightness(110%)"
  },
  vintage: {
    name: "Vintage",
    style: { filter: "sepia(50%) hue-rotate(-30deg) saturate(140%)" },
    preview: "sepia(50%) hue-rotate(-30deg) saturate(140%)"
  },
  cool: {
    name: "Froid",
    style: { filter: "saturate(80%) hue-rotate(30deg)" },
    preview: "saturate(80%) hue-rotate(30deg)"
  },
  warm: {
    name: "Chaud",
    style: { filter: "saturate(120%) hue-rotate(-20deg)" },
    preview: "saturate(120%) hue-rotate(-20deg)"
  },
};

const KEYBOARD_SHORTCUTS = {
  "R": "Réinitialiser rotation",
  "←/→": "Rotation ±5°",
  "Shift + ←/→": "Rotation ±15°",
  "Ctrl+Z": "Annuler",
  "Ctrl+Y": "Refaire",
  "Ctrl+S": "Sauvegarder dans la galerie",
  "Esc": "Réinitialiser tout",
};

const DEFAULT_SETTINGS = {
  rotation: 0,
  brightness: 100,
  contrast: 100,
  saturation: 100,
  hue: 0,
  filter: "none",
  quality: 60,
};

const CANVAS_SIZE = 800;
const PREVIEW_SIZE = 250;
const MAX_HISTORY = 15;
const PREVIEW_DELAY = 150;
const INITIAL_PREVIEW_DELAY = 300;

// ==================== UTILITY FUNCTIONS ====================
function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 80,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

function getQualityLabel(quality) {
  if (quality <= 30) return "Économique";
  if (quality <= 60) return "Standard";
  if (quality <= 85) return "Haute";
  return "Maximum";
}

function generateUniqueFileName(originalName) {
  const timestamp = Date.now();
  const randomStr = Math.random().toString(36).substring(2, 8);
  const baseName = originalName ? originalName.split('.')[0] : 'edited_image';
  return `${baseName}_edited_${timestamp}_${randomStr}.jpg`;
}

function getImageDimensions(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      resolve({ width: 800, height: 800 });
    };
    img.src = dataUrl;
  });
}

function isValidCrop(crop) {
  return crop && 
    typeof crop.x === 'number' && 
    typeof crop.y === 'number' && 
    typeof crop.width === 'number' && 
    typeof crop.height === 'number' &&
    crop.width > 0 && 
    crop.height > 0;
}

function debounce(func, delay) {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
}

// ==================== CUSTOM HOOKS ====================
function useHistory(initialState) {
  const [history, setHistory] = useState([initialState]);
  const [historyIndex, setHistoryIndex] = useState(0);

  const saveToHistory = useCallback((state) => {
    setHistory(prevHistory => {
      const newHistory = prevHistory.slice(0, historyIndex + 1);
      newHistory.push(state);

      if (newHistory.length > MAX_HISTORY) {
        newHistory.shift();
        setHistoryIndex(newHistory.length - 1);
        return newHistory;
      }

      setHistoryIndex(newHistory.length - 1);
      return newHistory;
    });
  }, [historyIndex]);

  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      return history[newIndex];
    }
    return null;
  }, [history, historyIndex]);

  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      return history[newIndex];
    }
    return null;
  }, [history, historyIndex]);

  const reset = useCallback((newInitialState) => {
    setHistory([newInitialState]);
    setHistoryIndex(0);
  }, []);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  return { saveToHistory, undo, redo, reset, canUndo, canRedo };
}

function useFileUpload() {
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e, onFileSelect) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      onFileSelect(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileInput = useCallback((e, onFileSelect) => {
    if (e.target.files && e.target.files[0]) {
      onFileSelect(e.target.files[0]);
    }
  }, []);

  return { dragActive, handleDrag, handleDrop, handleFileInput };
}

function useImageProcessing() {
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewSize, setPreviewSize] = useState("0 KB");
  const updateTimeoutRef = useRef(null);
  const previewBlobRef = useRef(null);

  const generatePreview = useCallback((
    imageSrc, 
    imgRef, 
    canvasRef, 
    previewCanvasRef, 
    completedCrop, 
    settings
  ) => {
    if (!imageSrc || !imgRef.current || !canvasRef.current || 
        !previewCanvasRef.current || !isValidCrop(completedCrop)) {
      return;
    }

    const image = imgRef.current;
    
    if (!image.complete || image.naturalWidth === 0 || image.naturalHeight === 0) {
      return;
    }

    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const previewCanvas = previewCanvasRef.current;
      const previewCtx = previewCanvas.getContext("2d");

      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      
      const pixelCrop = {
        x: completedCrop.x * scaleX,
        y: completedCrop.y * scaleY,
        width: completedCrop.width * scaleX,
        height: completedCrop.height * scaleY,
      };

      const sourceX = Math.max(0, Math.min(pixelCrop.x, image.naturalWidth));
      const sourceY = Math.max(0, Math.min(pixelCrop.y, image.naturalHeight));
      const sourceWidth = Math.min(pixelCrop.width, image.naturalWidth - sourceX);
      const sourceHeight = Math.min(pixelCrop.height, image.naturalHeight - sourceY);

      if (sourceWidth <= 0 || sourceHeight <= 0) {
        return;
      }

      const drawCroppedImage = (context, canvasSize) => {
        context.save();
        
        context.canvas.width = canvasSize;
        context.canvas.height = canvasSize;
        context.clearRect(0, 0, canvasSize, canvasSize);

        context.translate(canvasSize / 2, canvasSize / 2);

        if (settings.rotation !== 0) {
          context.rotate((settings.rotation * Math.PI) / 180);
        }

        const maxDim = Math.max(sourceWidth, sourceHeight);
        const scale = (canvasSize * 0.95) / maxDim;

        context.scale(scale, scale);

        let filterString = `brightness(${settings.brightness}%) contrast(${settings.contrast}%) saturate(${settings.saturation}%) hue-rotate(${settings.hue}deg)`;
        
        if (settings.filter !== "none" && FILTERS[settings.filter]?.style.filter) {
          filterString += " " + FILTERS[settings.filter].style.filter;
        }
        
        context.filter = filterString;

        const destX = -sourceWidth / 2;
        const destY = -sourceHeight / 2;

        context.drawImage(
          image,
          sourceX,
          sourceY,
          sourceWidth,
          sourceHeight,
          destX,
          destY,
          sourceWidth,
          sourceHeight
        );

        context.restore();
      };

      drawCroppedImage(ctx, CANVAS_SIZE);
      drawCroppedImage(previewCtx, PREVIEW_SIZE);

      if (previewBlobRef.current) {
        URL.revokeObjectURL(previewBlobRef.current);
        previewBlobRef.current = null;
      }

      previewCanvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          previewBlobRef.current = url;
          setPreviewUrl(url);
        }
      }, "image/jpeg", settings.quality / 100);

      canvas.toBlob((blob) => {
        if (blob) {
          const size = (blob.size / 1024).toFixed(2);
          setPreviewSize(size + " KB");
        }
      }, "image/jpeg", settings.quality / 100);

    } catch (error) {
      console.error("Erreur lors de la génération du preview:", error);
    }
  }, []);

  const schedulePreviewUpdate = useCallback((
    imageSrc, imgRef, canvasRef, previewCanvasRef, completedCrop, settings, delay = PREVIEW_DELAY
  ) => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    setIsGeneratingPreview(true);
    updateTimeoutRef.current = setTimeout(() => {
      generatePreview(imageSrc, imgRef, canvasRef, previewCanvasRef, completedCrop, settings);
      setIsGeneratingPreview(false);
    }, delay);
  }, [generatePreview]);

  const generateInitialPreview = useCallback((
    imageSrc, imgRef, canvasRef, previewCanvasRef, completedCrop, settings
  ) => {
    if (!imageSrc || !isValidCrop(completedCrop) || !imgRef.current) {
      return;
    }

    const img = imgRef.current;
    if (img.complete) {
      schedulePreviewUpdate(imageSrc, imgRef, canvasRef, previewCanvasRef, completedCrop, settings, INITIAL_PREVIEW_DELAY);
    } else {
      const handleLoad = () => {
        schedulePreviewUpdate(imageSrc, imgRef, canvasRef, previewCanvasRef, completedCrop, settings, INITIAL_PREVIEW_DELAY);
        img.removeEventListener('load', handleLoad);
      };
      img.addEventListener('load', handleLoad);
    }
  }, [schedulePreviewUpdate]);

  const cleanup = useCallback(() => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    if (previewBlobRef.current) {
      URL.revokeObjectURL(previewBlobRef.current);
      previewBlobRef.current = null;
    }
    setPreviewUrl(null);
    setPreviewSize("0 KB");
    setIsGeneratingPreview(false);
  }, []);

  return {
    isGeneratingPreview,
    previewUrl,
    previewSize,
    schedulePreviewUpdate,
    generateInitialPreview,
    cleanup
  };
}

function useKeyboardShortcuts(imageSrc, handlers) {
  const handleKeyDown = useCallback((e) => {
    if (!imageSrc || !handlers) return;

    // Prevent shortcuts when typing in inputs
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
      return;
    }

    switch (e.key) {
      case "z":
      case "Z":
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          handlers.undo?.();
        }
        break;
      case "r":
      case "R":
        e.preventDefault();
        handlers.resetRotation?.();
        break;
      case "ArrowLeft":
        e.preventDefault();
        handlers.rotateLeft?.(e.shiftKey ? 15 : 5);
        break;
      case "ArrowRight":
        e.preventDefault();
        handlers.rotateRight?.(e.shiftKey ? 15 : 5);
        break;
      case "y":
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          handlers.redo?.();
        }
        break;
      case "s":
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          handlers.save?.();
        }
        break;
      case "Escape":
        e.preventDefault();
        handlers.resetAll?.();
        break;
      default:
        break;
    }
  }, [imageSrc, handlers]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);
}

// ==================== COMPONENTS ====================
const FileUploadPanel = React.memo(({ imageSrc, fileInfo, imageSize, dragActive, onDrag, onDrop, onFileInput, onClear }) => (
  <div className="panel">
    <div className="panel-title">
      <span><i className="bi bi-upload me-2"></i>Image source</span>
      {imageSrc && (
        <button className="btn-reset" onClick={onClear}>
          <i className="bi bi-x-circle me-1"></i>
          Effacer
        </button>
      )}
    </div>

    {!imageSrc ? (
      <div
        className={`dropzone ${dragActive ? 'active' : ''}`}
        onDragEnter={onDrag}
        onDragOver={onDrag}
        onDragLeave={onDrag}
        onDrop={onDrop}
        onClick={() => document.getElementById("file-upload").click()}
      >
        <div className="dropzone-content">
          <i className="bi bi-cloud-upload display-4"></i>
          <h4>Glissez-déposez une image</h4>
          <p>ou cliquez pour parcourir</p>
          <small>Formats supportés: JPG, PNG, GIF</small>
        </div>
        <input
          id="file-upload"
          type="file"
          accept="image/*"
          onChange={onFileInput}
          className="hidden"
        />
      </div>
    ) : (
      <div className="file-info">
        <div className="file-details">
          <div className="file-icon">
            <i className="bi bi-file-earmark-image"></i>
          </div>
          <div className="file-data">
            <strong>{fileInfo?.name}</strong>
            <div className="file-meta">
              <span>{fileInfo?.size}</span>
              <span>•</span>
              <span>{imageSize.width} × {imageSize.height}</span>
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
));

const RotationPanel = React.memo(({ rotation, onRotationChange }) => (
  <div className="panel">
    <div className="panel-title">
      <span><i className="bi bi-arrow-repeat me-2"></i>Transformation</span>
      <button
        className="btn-reset"
        onClick={() => onRotationChange(0)}
      >
        Réinitialiser
      </button>
    </div>

    <div className="control-group">
      <div className="control-label">
        <span>Rotation</span>
        <span className="slider-value">{rotation}°</span>
      </div>
      <div className="slider-container">
        <input
          type="range"
          min="-180"
          max="180"
          step="1"
          value={rotation}
          onChange={(e) => onRotationChange(parseInt(e.target.value))}
          className="slider"
        />
      </div>
      <div className="rotation-controls">
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => onRotationChange(rotation - 90)}
          title="Rotation -90°"
        >
          <i className="bi bi-arrow-counterclockwise"></i> 90°
        </button>
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => onRotationChange(rotation - 5)}
          title="Rotation -5°"
        >
          <i className="bi bi-chevron-left"></i> 5°
        </button>
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => onRotationChange(rotation + 5)}
          title="Rotation +5°"
        >
          5° <i className="bi bi-chevron-right"></i>
        </button>
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => onRotationChange(rotation + 90)}
          title="Rotation +90°"
        >
          90° <i className="bi bi-arrow-clockwise"></i>
        </button>
      </div>
    </div>
  </div>
));

const FiltersPanel = React.memo(({ filter, onFilterChange }) => (
  <div className="panel">
    <div className="panel-title">
      <span><i className="bi bi-palette me-2"></i>Filtres rapides</span>
    </div>
    <div className="filters-container">
      {Object.entries(FILTERS).map(([key, value]) => (
        <button
          key={key}
          className={`filter-btn ${filter === key ? 'active' : ''}`}
          onClick={() => onFilterChange(key)}
          style={{ 
            backgroundImage: filter === key ? 'none' : `linear-gradient(45deg, transparent 30%, rgba(0,0,0,0.1) 100%)`,
            filter: value.preview,
          }}
        >
          {value.name}
        </button>
      ))}
    </div>
  </div>
));

const AdjustmentsPanel = React.memo(({ 
  brightness, contrast, saturation, hue, 
  showAdvanced, setShowAdvanced,
  onBrightnessChange, onContrastChange, onSaturationChange, onHueChange,
  onReset 
}) => {
  const getHueColor = useCallback(() => `hsl(${hue}, 100%, 50%)`, [hue]);

  return (
    <div className="panel">
      <div className="panel-title">
        <span><i className="bi bi-sliders me-2"></i>Ajustements</span>
        <div className="panel-controls">
          <button
            className="btn-toggle"
            onClick={() => setShowAdvanced(!showAdvanced)}
            title={showAdvanced ? "Masquer" : "Afficher"}
          >
            <i className={`bi bi-chevron-${showAdvanced ? 'up' : 'down'}`}></i>
          </button>
          <button className="btn-reset" onClick={onReset}>
            Réinitialiser
          </button>
        </div>
      </div>

      {showAdvanced && (
        <>
          <div className="control-group">
            <div className="control-label">
              <span>Luminosité</span>
              <span className="slider-value">{brightness}%</span>
            </div>
            <div className="slider-container">
              <input
                type="range"
                min="0"
                max="200"
                value={brightness}
                onChange={(e) => onBrightnessChange(parseInt(e.target.value))}
                className="slider"
              />
            </div>
          </div>

          <div className="control-group">
            <div className="control-label">
              <span>Contraste</span>
              <span className="slider-value">{contrast}%</span>
            </div>
            <div className="slider-container">
              <input
                type="range"
                min="0"
                max="200"
                value={contrast}
                onChange={(e) => onContrastChange(parseInt(e.target.value))}
                className="slider"
              />
            </div>
          </div>

          <div className="control-group">
            <div className="control-label">
              <span>Saturation</span>
              <span className="slider-value">{saturation}%</span>
            </div>
            <div className="slider-container">
              <input
                type="range"
                min="0"
                max="200"
                value={saturation}
                onChange={(e) => onSaturationChange(parseInt(e.target.value))}
                className="slider"
              />
            </div>
          </div>

          <div className="control-group">
            <div className="control-label">
              <span>Teinte</span>
              <div className="control-value">
                <span className="slider-value">{hue}°</span>
                <div
                  className="color-swatch"
                  style={{ backgroundColor: getHueColor() }}
                />
              </div>
            </div>
            <div className="slider-container">
              <input
                type="range"
                min="0"
                max="360"
                value={hue}
                onChange={(e) => onHueChange(parseInt(e.target.value))}
                className="slider slider-hue"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
});

const ExportPanel = React.memo(({ 
  quality, previewSize, previewUrl, isSaving,
  onQualityChange, onSaveToGallery, onDownload 
}) => (
  <div className="panel">
    <div className="panel-title">
      <span><i className="bi bi-download me-2"></i>Export</span>
    </div>

    <div className="control-group">
      <div className="control-label">
        <span>Qualité</span>
        <span className="slider-value">{quality}%</span>
      </div>
      <div className="slider-container">
        <input
          type="range"
          min="20"
          max="100"
          value={quality}
          onChange={(e) => onQualityChange(parseInt(e.target.value))}
          className="slider"
        />
      </div>
      <div className="quality-info">
        <span className="quality-label">{getQualityLabel(quality)}</span>
        <span className="size-estimate">{previewSize}</span>
      </div>
    </div>

    <div className="export-info">
      <div className="export-detail">
        <i className="bi bi-aspect-ratio me-2"></i>
        <span>800 × 800 pixels</span>
      </div>
      <div className="export-detail">
        <i className="bi bi-file-earmark me-2"></i>
        <span>Format JPEG</span>
      </div>
    </div>

    <div className="export-buttons">
      <button
        className="btn btn-success btn-save"
        onClick={onSaveToGallery}
        disabled={!previewUrl || isSaving}
        title="Sauvegarder dans la galerie (Ctrl+S)"
      >
        {isSaving ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
            Sauvegarde...
          </>
        ) : (
          <>
            <i className="bi bi-cloud-plus me-2"></i>
            Sauvegarder dans la galerie
          </>
        )}
      </button>

      <button
        className="btn btn-outline-primary btn-download"
        onClick={onDownload}
        disabled={!previewUrl}
        title="Télécharger l'image"
      >
        <i className="bi bi-download me-2"></i>
        Télécharger
      </button>
    </div>
  </div>
));

const KeyboardShortcutsPanel = React.memo(() => {
  const keyboardShortcutsList = useMemo(() => {
    return Object.entries(KEYBOARD_SHORTCUTS).map(([key, description]) => (
      <div key={key} className="shortcut-item">
        <span className="shortcut-key">{key}</span>
        <span className="shortcut-desc">{description}</span>
      </div>
    ));
  }, []);

  return (
    <div className="panel">
      <div className="panel-title">
        <span><i className="bi bi-keyboard me-2"></i>Raccourcis clavier</span>
      </div>
      <div className="shortcuts-list">
        {keyboardShortcutsList}
      </div>
    </div>
  );
});

// ==================== MAIN COMPONENT ====================
function AdminImageEditor() {
  const navigate = useNavigate();
  
  // Image state
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [fileInfo, setFileInfo] = useState(null);
  
  // Settings state
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Refs
  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  const previewCanvasRef = useRef(null);
  
  // Custom hooks
  const { dragActive, handleDrag, handleDrop, handleFileInput } = useFileUpload();
  const { 
    isGeneratingPreview, 
    previewUrl, 
    previewSize, 
    schedulePreviewUpdate,
    generateInitialPreview,
    cleanup: cleanupPreview 
  } = useImageProcessing();
  
  const initialHistoryState = useMemo(() => ({
    crop: null,
    ...DEFAULT_SETTINGS
  }), []);
  
  const { saveToHistory, undo, redo, reset: resetHistory, canUndo, canRedo } = useHistory(initialHistoryState);

  // Debounced history save
  const debouncedSaveToHistory = useMemo(
    () => debounce((state) => saveToHistory(state), 500),
    [saveToHistory]
  );

  // Update settings with history
  const updateSetting = useCallback((key, value) => {
    setSettings(prev => {
      const newSettings = { ...prev, [key]: value };
      debouncedSaveToHistory({
        crop: completedCrop,
        ...newSettings
      });
      return newSettings;
    });
  }, [completedCrop, debouncedSaveToHistory]);

  // File handling
  const handleFile = useCallback((file) => {
    if (!file.type.match("image.*")) {
      alert("Veuillez sélectionner une image valide");
      return;
    }

    cleanupPreview();

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        setImageSize({ width: img.width, height: img.height });
        setImageSrc(e.target.result);
        const newCrop = centerAspectCrop(img.width, img.height, 1);
        setCrop(newCrop);
        setCompletedCrop(newCrop);
        setSettings(DEFAULT_SETTINGS);

        const initialState = {
          crop: newCrop,
          ...DEFAULT_SETTINGS
        };
        resetHistory(initialState);

        // Generate initial preview after state is set
        setTimeout(() => {
          generateInitialPreview(
            e.target.result,
            imgRef,
            canvasRef,
            previewCanvasRef,
            newCrop,
            DEFAULT_SETTINGS
          );
        }, 100);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);

    setFileInfo({
      name: file.name,
      type: file.type,
      size: (file.size / 1024).toFixed(2) + " KB",
    });
  }, [cleanupPreview, resetHistory, generateInitialPreview]);

  // Clear image
  const clearImage = useCallback(() => {
    setImageSrc(null);
    setCrop(null);
    setCompletedCrop(null);
    setFileInfo(null);
    setImageSize({ width: 0, height: 0 });
    setSettings(DEFAULT_SETTINGS);
    cleanupPreview();
    resetHistory(initialHistoryState);
  }, [cleanupPreview, resetHistory, initialHistoryState]);

  // History handlers
  const handleUndo = useCallback(() => {
    const prevState = undo();
    if (prevState) {
      setCrop(prevState.crop);
      setCompletedCrop(prevState.crop);
      setSettings({
        rotation: prevState.rotation,
        brightness: prevState.brightness,
        contrast: prevState.contrast,
        saturation: prevState.saturation,
        hue: prevState.hue,
        filter: prevState.filter,
        quality: prevState.quality || DEFAULT_SETTINGS.quality,
      });
    }
  }, [undo]);

  const handleRedo = useCallback(() => {
    const nextState = redo();
    if (nextState) {
      setCrop(nextState.crop);
      setCompletedCrop(nextState.crop);
      setSettings({
        rotation: nextState.rotation,
        brightness: nextState.brightness,
        contrast: nextState.contrast,
        saturation: nextState.saturation,
        hue: nextState.hue,
        filter: nextState.filter,
        quality: nextState.quality || DEFAULT_SETTINGS.quality,
      });
    }
  }, [redo]);

  // Reset functions
  const resetAll = useCallback(() => {
    if (imageSrc) {
      const img = new Image();
      img.onload = () => {
        const newCrop = centerAspectCrop(img.width, img.height, 1);
        setCrop(newCrop);
        setCompletedCrop(newCrop);
        setSettings(DEFAULT_SETTINGS);
      };
      img.src = imageSrc;
    }
  }, [imageSrc]);

  const resetAdjustments = useCallback(() => {
    setSettings(prev => ({
      ...prev,
      brightness: 100,
      contrast: 100,
      saturation: 100,
      hue: 0,
    }));
  }, []);

  // Save to gallery
  const saveToGallery = useCallback(async () => {
    if (!canvasRef.current || !previewUrl) {
      alert("Aucune image à sauvegarder");
      return;
    }

    setIsSaving(true);

    try {
      canvasRef.current.toBlob(async (blob) => {
        if (!blob) {
          alert("Erreur lors de la génération de l'image");
          setIsSaving(false);
          return;
        }

        const reader = new FileReader();
        reader.onload = async () => {
          try {
            const dimensions = await getImageDimensions(reader.result);
            const uniqueName = generateUniqueFileName(fileInfo?.name);
            const imageData = {
              id: `img_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`,
              name: uniqueName,
              originalName: fileInfo?.name || 'image_éditée.jpg',
              dataUrl: reader.result,
              size: blob.size,
              type: 'image/jpeg',
              createdAt: new Date().toISOString(),
              dimensions: dimensions,
              metadata: {
                editedWith: 'AdminImageEditor',
                filters: settings
              }
            };

            const existingImages = JSON.parse(localStorage.getItem('adminGallery') || '[]');
            const updatedImages = [...existingImages, imageData];
            localStorage.setItem('adminGallery', JSON.stringify(updatedImages));

            setIsSaving(false);
            setSaveSuccess(true);

            setTimeout(() => {
              setSaveSuccess(false);
            }, 4000);

          } catch (error) {
            console.error('Erreur lors de la sauvegarde:', error);
            alert('Erreur lors de la sauvegarde dans la galerie');
            setIsSaving(false);
          }
        };

        reader.onerror = () => {
          alert('Erreur lors de la lecture de l\'image');
          setIsSaving(false);
        };

        reader.readAsDataURL(blob);
      }, "image/jpeg", settings.quality / 100);

    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Erreur lors de la sauvegarde');
      setIsSaving(false);
    }
  }, [settings.quality, fileInfo, previewUrl, settings]);

  // Download
  const handleDownload = useCallback(() => {
    if (!canvasRef.current) return;

    canvasRef.current.toBlob(
      (blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;

          const originalName = fileInfo?.name || "image";
          const baseName =
            originalName.substring(0, originalName.lastIndexOf(".")) ||
            originalName;
          link.download = `${baseName}_edited.jpg`;

          link.click();
          URL.revokeObjectURL(url);
        }
      },
      "image/jpeg",
      settings.quality / 100
    );
  }, [settings.quality, fileInfo]);

  // Filter style for preview
  const getFilterStyle = useCallback(() => {
    let filterCSS = `brightness(${settings.brightness}%) contrast(${settings.contrast}%) saturate(${settings.saturation}%) hue-rotate(${settings.hue}deg)`;

    if (settings.filter !== "none" && FILTERS[settings.filter]?.style.filter) {
      filterCSS += " " + FILTERS[settings.filter].style.filter;
    }

    return {
      filter: filterCSS,
      transform: `rotate(${settings.rotation}deg)`,
      transformOrigin: "center center",
    };
  }, [settings]);

  // Crop handlers
  const handleCropChange = useCallback((newCrop) => {
    setCrop(newCrop);
  }, []);

  const handleCropComplete = useCallback((newCrop) => {
    setCompletedCrop(newCrop);
  }, []);

  const handleClose = useCallback(() => {
    if (
      window.confirm(
        "Êtes-vous sûr de vouloir quitter l'éditeur ? Les modifications non sauvegardées seront perdues."
      )
    ) {
      navigate("/admin");
    }
  }, [navigate]);

  // Keyboard shortcuts handlers
  const keyboardHandlers = useMemo(() => ({
    undo: handleUndo,
    redo: handleRedo,
    resetRotation: () => updateSetting('rotation', 0),
    rotateLeft: (degrees) => updateSetting('rotation', settings.rotation - degrees),
    rotateRight: (degrees) => updateSetting('rotation', settings.rotation + degrees),
    save: saveToGallery,
    resetAll: resetAll,
  }), [handleUndo, handleRedo, updateSetting, settings.rotation, saveToGallery, resetAll]);

  useKeyboardShortcuts(imageSrc, keyboardHandlers);

  // Effects
  useEffect(() => {
    if (imageSrc && isValidCrop(completedCrop) && imgRef.current && imgRef.current.complete) {
      schedulePreviewUpdate(
        imageSrc, 
        imgRef, 
        canvasRef, 
        previewCanvasRef, 
        completedCrop, 
        settings
      );
    }
  }, [imageSrc, completedCrop, settings, schedulePreviewUpdate]);

  // Generate initial preview when image loads
  useEffect(() => {
    if (imageSrc && isValidCrop(completedCrop)) {
      generateInitialPreview(
        imageSrc,
        imgRef,
        canvasRef,
        previewCanvasRef,
        completedCrop,
        settings
      );
    }
  }, [imageSrc, completedCrop, generateInitialPreview, settings]);

  useEffect(() => {
    return () => {
      cleanupPreview();
    };
  }, [cleanupPreview]);

  // Event handlers for file upload
  const onDrag = useCallback((e) => handleDrag(e), [handleDrag]);
  const onDrop = useCallback((e) => handleDrop(e, handleFile), [handleDrop, handleFile]);
  const onFileInput = useCallback((e) => handleFileInput(e, handleFile), [handleFileInput, handleFile]);

  return (
    <div className="image-editor-container">
      {/* Header */}
      <div className="image-editor-header">
        <div className="header-left">
          <h1 className="image-editor-title">
            <i className="bi bi-magic me-2"></i>
            Éditeur d'image
          </h1>

          {imageSrc && (
            <div className="history-controls">
              <button
                className={`history-btn ${!canUndo ? 'history-btn-disabled' : ''}`}
                onClick={handleUndo}
                disabled={!canUndo}
                title="Annuler (Ctrl+Z)"
              >
                <i className="bi bi-arrow-counterclockwise"></i>
              </button>
              <button
                className={`history-btn ${!canRedo ? 'history-btn-disabled' : ''}`}
                onClick={handleRedo}
                disabled={!canRedo}
                title="Refaire (Ctrl+Y)"
              >
                <i className="bi bi-arrow-clockwise"></i>
              </button>
              <button
                className="history-btn"
                onClick={resetAll}
                title="Réinitialiser tout (Esc)"
              >
                <i className="bi bi-arrow-repeat"></i>
              </button>
            </div>
          )}
        </div>
        <div className="header-right">
          <Link
            to="/admin/gallery"
            className="btn btn-outline-primary"
            title="Voir la galerie"
          >
            <i className="bi bi-images me-2"></i>
            Galerie
          </Link>
          <button
            className="btn btn-secondary"
            onClick={handleClose}
          >
            <i className="bi bi-arrow-left me-2"></i>
            Retour Admin
          </button>
        </div>
      </div>

      {/* Success message */}
      {saveSuccess && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          <i className="bi bi-check-circle me-2"></i>
          Image sauvegardée avec succès dans la galerie !
          <button
            type="button"
            className="btn-close"
            onClick={() => setSaveSuccess(false)}
          ></button>
        </div>
      )}

      {/* Main work area */}
      <div className="work-area">
        {/* Tool panel */}
        <div className="tool-panel">
          <FileUploadPanel
            imageSrc={imageSrc}
            fileInfo={fileInfo}
            imageSize={imageSize}
            dragActive={dragActive}
            onDrag={onDrag}
            onDrop={onDrop}
            onFileInput={onFileInput}
            onClear={clearImage}
          />

          {imageSrc && (
            <>
              <RotationPanel
                rotation={settings.rotation}
                onRotationChange={(value) => updateSetting('rotation', value)}
              />

              <FiltersPanel
                filter={settings.filter}
                onFilterChange={(value) => updateSetting('filter', value)}
              />

              <AdjustmentsPanel
                brightness={settings.brightness}
                contrast={settings.contrast}
                saturation={settings.saturation}
                hue={settings.hue}
                showAdvanced={showAdvanced}
                setShowAdvanced={setShowAdvanced}
                onBrightnessChange={(value) => updateSetting('brightness', value)}
                onContrastChange={(value) => updateSetting('contrast', value)}
                onSaturationChange={(value) => updateSetting('saturation', value)}
                onHueChange={(value) => updateSetting('hue', value)}
                onReset={resetAdjustments}
              />

              <ExportPanel
                quality={settings.quality}
                previewSize={previewSize}
                previewUrl={previewUrl}
                isSaving={isSaving}
                onQualityChange={(value) => updateSetting('quality', value)}
                onSaveToGallery={saveToGallery}
                onDownload={handleDownload}
              />

              <KeyboardShortcutsPanel />
            </>
          )}
        </div>

        {/* Canvas area */}
        <div className="canvas-area">
          {!imageSrc ? (
            <div className="empty-state">
              <i className="bi bi-image display-1"></i>
              <h2>Aucune image sélectionnée</h2>
              <p>Utilisez le panneau de gauche pour charger une image ou glissez-déposez une image ici</p>
              <button
                className="btn btn-primary"
                onClick={() => document.getElementById("file-upload")?.click()}
              >
                <i className="bi bi-plus-circle me-2"></i>
                Choisir une image
              </button>
            </div>
          ) : (
            <div className="image-container">
              <div className="crop-container">
                <ReactCrop
                  crop={crop}
                  onChange={handleCropChange}
                  onComplete={handleCropComplete}
                  aspect={1}
                  circularCrop={false}
                  keepSelection={true}
                  minWidth={50}
                  minHeight={50}
                  className="custom-crop"
                >
                  <img
                    ref={imgRef}
                    src={imageSrc}
                    alt="Source"
                    style={getFilterStyle()}
                    className="crop-image"
                  />
                </ReactCrop>
              </div>

              {/* Final preview */}
              <div className="preview-panel">
                <div className="preview-header">
                  <h4><i className="bi bi-eye me-2"></i>Aperçu final</h4>
                  <div className="preview-status">
                    {isGeneratingPreview && <div className="status-indicator generating"></div>}
                    {!isGeneratingPreview && previewUrl && <div className="status-indicator ready"></div>}
                  </div>
                </div>
                <div className="preview-container">
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="Aperçu final"
                      className="preview-image"
                    />
                  ) : (
                    <div className="preview-placeholder">
                      <i className="bi bi-crop"></i>
                      <span>Définissez une zone de sélection</span>
                    </div>
                  )}
                </div>
                <div className="preview-info">
                  <span className="preview-size">{previewSize}</span>
                  <span className="preview-desc">Zone sélectionnée</span>
                </div>
              </div>
            </div>
          )}
          <canvas ref={canvasRef} className="hidden-canvas" />
          <canvas ref={previewCanvasRef} className="hidden-canvas" />
        </div>
      </div>

      {/* Status bar */}
      <div className="status-bar">
        <div className="status-left">
          {fileInfo && (
            <>
              <i className="bi bi-file-earmark-image me-2"></i>
              <span>{fileInfo.name}</span>
              <span className="separator">•</span>
              <span>Sortie: 800 × 800px</span>
            </>
          )}
        </div>
        <div className="status-right">
          {previewSize && (
            <>
              <i className="bi bi-hdd me-2"></i>
              <span>{previewSize}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminImageEditor;